import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Icon, Spin, Select, Tabs, Button, message } from "antd";
import { CaseService } from "services";
import SectionBlock from "./components/section-block";
import CardBlock from "./components/card-block";
import PortfolioTable from "./components/portfolio-table";
import PortfolioCasesTable from "./components/portfolio-cases-table";
import GroupInfoTable from "components/group-info-table";
import {
    SECTION_IDS,
    PORTFOLIO_LIMIT_COLUMNS,
    PORTFOLIO_VIEW_COLUMNS
} from "./constants";
import { MONEY_UNIT_OPTIONS } from "./money-format";

const { Option } = Select;
const { TabPane } = Tabs;

// Comments can only be ADDED at the credit stage — same gate the Case Summary
// tab applies, so the two views agree on when the thread is open.
const COMMENT_STAGE_ID = 8;

// Limit Info / Group Info rebuild and the Group Info Active toggle are only
// actionable at the credit stages, matching the gate used elsewhere in
// Credit Checker (TVR reset, bank statement rebuild).
const CREDIT_STAGE_IDS = [7, 8];

/**
 * Turns the Portfolio View "Comments" column into the drawer trigger, matching
 * the Case Summary tab (same component, same endpoints — one comment thread per
 * portfolio-instance row, not a second one). Rows with no instance id can't carry
 * a remark, and a row with no remarks outside the credit stage stays inert.
 */
function withCommentsAction(columns, stageId, onOpen) {
    if (!onOpen) {
        return columns;
    }
    return columns.map(col => {
        if (col.key !== "comments") {
            return col;
        }
        return {
            ...col,
            render: (_, record) => {
                const canComment =
                    record &&
                    record.portfolioInstanceId != null &&
                    record.key;
                const count = (record && record.remark_counts) || 0;
                const canAdd = Number(stageId) === COMMENT_STAGE_ID;
                if (!canComment || (count === 0 && !canAdd)) {
                    return "—";
                }
                const last = record.last_remark_on
                    ? moment(record.last_remark_on).format("DD MMM YYYY")
                    : null;
                // Reuses the Case Summary tab's button class (its stylesheet is
                // already loaded by the drawer), so both views look the same.
                return (
                    <button
                        type={"button"}
                        className={"portfolio-comments-btn"}
                        onClick={() => onOpen(record)}
                        title={count ? "View / add comments" : "Add a comment"}
                    >
                        <Icon type={"message"} />
                        <span>{count}</span>
                        {last ? (
                            <React.Fragment>
                                <span>{"·"}</span>
                                <span>{last}</span>
                            </React.Fragment>
                        ) : null}
                    </button>
                );
            }
        };
    });
}

/**
 * Existing Relationship section — the borrower's existing exposure with Progcap:
 *  - Portfolio View: aggregated performance by dimension (Seller / Anchor / Geo
 *    / Industry / RBH) — vintage, customers, % active, PAR buckets, AUM, etc.
 *  - Limit Info: product-wise limits / OS / overdue, with a Lacs/Cr/Full toggle.
 *  - All Cases: detailed per-case performance grid (sort / filter / frozen cols).
 */
class ExistingPortfolioSection extends React.Component {
    state = {
        limitUnit: "lacs",
        groupInfoActive: "active",
        // Row-count reported by each GroupInfoTable via onCountChange —
        // rendered next to the tab label so the reviewer sees "Active (3)".
        groupInfoActiveCount: null,
        groupInfoInactiveCount: null,
        // GroupInfoTable does not refetch itself after its Active toggle PATCHes
        // — it only reloads when this key changes. Without it the switch flips
        // back to the stale row and the toggle looks broken.
        groupInfoRefreshKey: 0,
        groupInfoRebuilding: false,
        // Reported by the "All" GroupInfoTable (the complete, unfiltered set
        // of group entities) via onValidationChange — false until that table
        // has actually fetched, so the Approve button never shows "Approved"
        // before the real data says so.
        groupInfoAllValidated: false,
        groupInfoApproving: false
    };

    // Rebuild Group Info from source (GET /case/:id/dedupe/?force_build=true),
    // then bump the refresh key so both the Active and All tables reload with
    // the freshly rebuilt matches. onGroupInfoUpdated also refreshes Total
    // Exposure — see its comment below.
    handleRebuildGroupInfo = () => {
        const { caseId } = this.props;
        if (!caseId || this.state.groupInfoRebuilding) {
            return;
        }
        this.setState({ groupInfoRebuilding: true });
        CaseService.getCaseDedupe(caseId, false, true)
            .then(() => {
                message.success("Group Info rebuilt");
                this.onGroupInfoUpdated();
            })
            .catch(() => message.error("Failed to rebuild Group Info"))
            .finally(() => this.setState({ groupInfoRebuilding: false }));
    };

    setUnit = limitUnit => this.setState({ limitUnit });
    setGroupInfoTab = groupInfoActive => this.setState({ groupInfoActive });
    setGroupInfoActiveCount = count =>
        this.setState({ groupInfoActiveCount: count });
    setGroupInfoInactiveCount = count =>
        this.setState({ groupInfoInactiveCount: count });
    // Mirrors the flag into Redux too (onGroupInfoValidationChange), not just
    // local state — the stage 7/8 proceed checklist runs in redux middleware,
    // away from this component, and has no other way to read it.
    setGroupInfoAllValidated = allValidated => {
        const validated = Boolean(allValidated);
        this.setState({ groupInfoAllValidated: validated });
        if (this.props.onGroupInfoValidationChange) {
            this.props.onGroupInfoValidationChange(
                this.props.caseId,
                validated
            );
        }
    };

    // Approve — records reviewer sign-off on the case's group entities
    // (POST /case/:id/dedupe/, no body). Bumps the refresh key afterwards so
    // both child tables refetch and groupInfoAllValidated is re-derived from
    // the latest rows, rather than being forced true optimistically here.
    handleApproveGroupInfo = () => {
        const { caseId } = this.props;
        if (!caseId || this.state.groupInfoApproving) {
            return;
        }
        this.setState({ groupInfoApproving: true });
        CaseService.approveCaseDedupe(caseId)
            .then(() => {
                message.success("Group Info approved");
                this.onGroupInfoUpdated();
            })
            .catch(() => message.error("Failed to approve Group Info"))
            .finally(() => this.setState({ groupInfoApproving: false }));
    };

    // Fired after a Group Info rebuild AND after each row's Active/Inactive
    // switch flip (GroupInfoTable calls this same prop on both). Bumps the
    // refresh key so BOTH tables reload — the row moves between the Active and
    // All sets, so their counts change together — and also asks the parent to
    // recompute Total Exposure, since its group_exposure figure is derived
    // from which group entities are currently active.
    onGroupInfoUpdated = () => {
        this.setState(prev => ({
            groupInfoRefreshKey: prev.groupInfoRefreshKey + 1
        }));
        if (this.props.onGroupInfoRebuilt) {
            this.props.onGroupInfoRebuilt();
        }
    };

    render() {
        const {
            innerRef,
            portfolioView,
            portfolioLoading,
            limitInfo,
            allCases,
            limitLoading,
            casesLoading,
            expanded,
            onToggle,
            stageId,
            onOpenPortfolioRemarks,
            onRebuildLimitInfo,
            onFiMovementDone,
            permissionContext,
            recreditViewOnlyPermission
        } = this.props;

        const portfolioColumns = withCommentsAction(
            PORTFOLIO_VIEW_COLUMNS,
            stageId,
            onOpenPortfolioRemarks
        );
        const canEditAtCreditStage = CREDIT_STAGE_IDS.includes(
            Number(stageId)
        );

        return (
            <SectionBlock
                id={SECTION_IDS.EXISTING_PORTFOLIO}
                innerRef={innerRef}
                title={"Existing Relationship"}
                subtitle={"Existing limits, exposure and linked cases"}
                icon={"pie-chart"}
                expanded={expanded}
                onToggle={onToggle}
            >
                <CardBlock
                    icon={"pie-chart"}
                    title={"Portfolio View"}
                    className={"cc-card--tint-blue"}
                    subtitle={
                        "Aggregated portfolio performance by seller, anchor, geography, industry and RBH"
                    }
                >
                    <Spin spinning={Boolean(portfolioLoading)}>
                        <PortfolioTable
                            columns={portfolioColumns}
                            rows={portfolioView}
                            emptyText={"Portfolio data not available"}
                        />
                    </Spin>
                </CardBlock>

                <CardBlock
                    icon={"profile"}
                    title={"Limit Info"}
                    className={"cc-card--tint-violet"}
                    subtitle={
                        "Product-wise sanctioned limits, current OS and overdue"
                    }
                    action={
                        <span className={"cc-pf-unit"}>
                            {onRebuildLimitInfo && canEditAtCreditStage ? (
                                <Button
                                    size={"small"}
                                    icon={"reload"}
                                    loading={Boolean(limitLoading)}
                                    onClick={onRebuildLimitInfo}
                                    title={
                                        "Re-derive limit info from source (force build)"
                                    }
                                >
                                    Rebuild
                                </Button>
                            ) : null}
                            <span className={"cc-pf-unit__label"}>Show in</span>
                            <Select
                                size={"small"}
                                value={this.state.limitUnit}
                                onChange={this.setUnit}
                                dropdownMatchSelectWidth={false}
                            >
                                {MONEY_UNIT_OPTIONS.map(o => (
                                    <Option key={o.key} value={o.key}>
                                        {o.label}
                                    </Option>
                                ))}
                            </Select>
                        </span>
                    }
                >
                    <Spin spinning={Boolean(limitLoading)}>
                        <PortfolioTable
                            columns={PORTFOLIO_LIMIT_COLUMNS}
                            rows={limitInfo}
                            unit={this.state.limitUnit}
                            emptyText={"Limit info not available"}
                            stickyCount={2}
                        />
                    </Spin>
                </CardBlock>

                <CardBlock
                    icon={"profile"}
                    collapsible
                    defaultCollapsed
                    title={`All Cases${
                        allCases && allCases.length
                            ? ` (${allCases.length})`
                            : ""
                    }`}
                    subtitle={
                        "Per-case performance — sort any column, filter, first columns frozen"
                    }
                >
                    <PortfolioCasesTable
                        rows={allCases}
                        loading={Boolean(casesLoading)}
                        onFiMovementDone={onFiMovementDone}
                        permissionContext={permissionContext}
                        recreditViewOnlyPermission={
                            recreditViewOnlyPermission
                        }
                    />
                </CardBlock>

                {/* Group Info — linked group entities from the dedupe API. Two
                    tabs: Active (default) and Inactive so reviewers can flip to
                    old / de-linked matches without leaving the tab. The table
                    itself already supports filterByActive; each tab passes the
                    matching value. */}
                <CardBlock
                    icon={"team"}
                    title={"Group Info"}
                    subtitle={
                        "Linked group entities matched on the dedupe API"
                    }
                    action={
                        canEditAtCreditStage ? (
                            <Button
                                size={"small"}
                                icon={"reload"}
                                loading={this.state.groupInfoRebuilding}
                                onClick={this.handleRebuildGroupInfo}
                            >
                                Rebuild
                            </Button>
                        ) : null
                    }
                >
                    <Tabs
                        activeKey={this.state.groupInfoActive}
                        onChange={this.setGroupInfoTab}
                        animated={false}
                    >
                        {/* forceRender=true on both panes: antd Tabs lazy-mounts
                            hidden TabPanes, which meant the Inactive
                            GroupInfoTable never ran its fetch until the user
                            clicked the tab — the Inactive count stayed hidden
                            in the label. Forcing render on mount fires both
                            fetches together so both counts show up front. */}
                        <TabPane
                            forceRender
                            tab={
                                <span>
                                    Active
                                    {this.state.groupInfoActiveCount !== null
                                        ? ` (${this.state.groupInfoActiveCount})`
                                        : ""}
                                </span>
                            }
                            key={"active"}
                        >
                            <GroupInfoTable
                                caseId={this.props.caseId}
                                filterByActive={true}
                                showSectionLabel={false}
                                onCountChange={this.setGroupInfoActiveCount}
                                groupInfoRefreshKey={
                                    this.state.groupInfoRefreshKey
                                }
                                onGroupInfoUpdated={this.onGroupInfoUpdated}
                                disableActiveToggle={!canEditAtCreditStage}
                            />
                        </TabPane>
                        <TabPane
                            forceRender
                            tab={
                                <span>
                                    All
                                    {this.state.groupInfoInactiveCount !== null
                                        ? ` (${this.state.groupInfoInactiveCount})`
                                        : ""}
                                </span>
                            }
                            key={"inactive"}
                        >
                            <GroupInfoTable
                                caseId={this.props.caseId}
                                filterByActive={false}
                                showSectionLabel={false}
                                onCountChange={this.setGroupInfoInactiveCount}
                                onValidationChange={
                                    this.setGroupInfoAllValidated
                                }
                                groupInfoRefreshKey={
                                    this.state.groupInfoRefreshKey
                                }
                                onGroupInfoUpdated={this.onGroupInfoUpdated}
                                disableActiveToggle={!canEditAtCreditStage}
                            />
                        </TabPane>
                    </Tabs>
                    {/* Approve — hidden outside the credit stages. Disabled
                        with a tick once every group entity (across both
                        tabs' underlying "All" set) is already validated;
                        otherwise enabled so a reviewer can sign off. */}
                    {canEditAtCreditStage ? (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                marginTop: 12,
                                paddingTop: 12,
                                borderTop: "1px solid #e6e9ee"
                            }}
                        >
                            <Button
                                type={"primary"}
                                icon={
                                    this.state.groupInfoAllValidated
                                        ? "check"
                                        : undefined
                                }
                                loading={this.state.groupInfoApproving}
                                disabled={this.state.groupInfoAllValidated}
                                onClick={this.handleApproveGroupInfo}
                            >
                                {this.state.groupInfoAllValidated
                                    ? "Approved"
                                    : "Approve"}
                            </Button>
                        </div>
                    ) : null}
                </CardBlock>
            </SectionBlock>
        );
    }
}

ExistingPortfolioSection.propTypes = {
    innerRef: PropTypes.object,
    caseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    portfolioView: PropTypes.array,
    portfolioLoading: PropTypes.bool,
    limitInfo: PropTypes.array,
    allCases: PropTypes.array,
    limitLoading: PropTypes.bool,
    casesLoading: PropTypes.bool,
    expanded: PropTypes.bool,
    onToggle: PropTypes.func,
    // Gates whether a NEW comment can be added (credit stage only).
    stageId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onOpenPortfolioRemarks: PropTypes.func,
    // Rebuilds Limit Info from source (force_build). Omit to hide the button.
    onRebuildLimitInfo: PropTypes.func,
    // Called after a Group Info rebuild resolves, so the parent can refresh
    // the Total Exposure card (its group_exposure figure depends on Group
    // Info). Omit to skip.
    onGroupInfoRebuilt: PropTypes.func,
    // Mirrors groupInfoAllValidated into redux (setGroupInfoAllValidated)
    // so the stage 7/8 proceed checklist can gate on it. Called with
    // (caseId, allValidated) every time it's re-derived.
    onGroupInfoValidationChange: PropTypes.func,
    onFiMovementDone: PropTypes.func,
    permissionContext: PropTypes.object,
    recreditViewOnlyPermission: PropTypes.bool
};

export default ExistingPortfolioSection;
